import type { ThemeProperties } from '@unifyapps/defs/theme/types';

/**
 * MIME types for the `type` attribute on <link rel="preload" as="font">.
 * Required so the browser matches the preload to the @font-face fetch and
 * avoids double-downloading.
 */
const MIME_BY_EXTENSION: Record<string, string> = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
  eot: 'application/vnd.ms-fontobject',
  svg: 'image/svg+xml',
};

export interface FontPreloadLink {
  href: string;
  type?: string;
}

// lowercased file extension from a (possibly query-suffixed) font URL; shared with the @font-face
// builder so preload `type` and `@font-face` `format()` derive from the same source
export const getFontFileExtension = (filePath: string): string | undefined => {
  const queryIndex = filePath.indexOf('?');
  const path = queryIndex === -1 ? filePath : filePath.slice(0, queryIndex);
  const dotIndex = path.lastIndexOf('.');
  if (dotIndex === -1) return undefined;
  return path.slice(dotIndex + 1).toLowerCase();
};

const getMimeFromPath = (filePath: string): string | undefined => {
  const ext = getFontFileExtension(filePath);
  return ext ? MIME_BY_EXTENSION[ext] : undefined;
};

/**
 * Builds <link rel="preload" as="font"> descriptors for every custom font file.
 * Emitted in <head> alongside the theme stylesheet so font files are fetched
 * in parallel with HTML parsing — preventing the font-display:swap flash that
 * happens when @font-face files are fetched lazily at first text paint.
 *
 * Mirrors next/font/google's preload behavior, but at request time since
 * theme-driven custom font URLs aren't knowable at build time.
 */
export const getCustomFontPreloadLinks = (
  customFonts: ThemeProperties['customFonts'] | undefined,
): FontPreloadLink[] => {
  if (!customFonts?.length) return [];

  const links: FontPreloadLink[] = [];
  for (const font of customFonts) {
    if (!font.files?.length) continue;
    for (const file of font.files) {
      if (!file.filePath) continue;
      links.push({ href: file.filePath, type: getMimeFromPath(file.filePath) });
    }
  }
  return links;
};
